<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import {
  fetchTutorial, saveTutorialStep, completeTutorial, skipTutorial,
} from '../game/tutorial-api';
import { tutorialLaunch, closeTutorial } from '../game/tutorial-launch';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import { t } from '../i18n';

const emit = defineEmits<{ (e: 'done'): void; (e: 'navigate', view: string): void; (e: 'updated'): void }>();

/** The screen behind the card for each beat — null keeps whatever backdrop is
 *  already showing (used for beats that don't describe one specific screen). */
type TargetView = 'profile' | 'map' | 'districts' | 'market' | 'jobs' | 'chat'
  | 'rankings' | 'dungeons' | 'pit' | 'reserve' | 'counting' | 'settings';

interface Step { title: string; body: string; view: TargetView | null }

// 10 onboarding beats — teach the (restored) spatial dominion loop, each parked
// on the screen it's actually talking about so the player previews it live.
// {cash}/{gold}/{nerve} tokens render as icon+name pairs — see renderBody().
// NOTE: these keys MUST match tutorial.steps.* in the locale files. Fresh
// main shipped them mismatched (iceRoads/fogOfWar/gearUp vs the locales'
// walk/fog/gear) — the tutorial rendered raw key paths. Aligned here, plus
// the two market steps the 06.08 ticket asked for (stock never rotates;
// gear wears and breaks but is never lost).
const STEP_KEYS = [
  'welcome', 'walk', 'fog', 'fight',
  'gear', 'tradestead', 'gearwear', 'ground', 'holmgang', 'raids', 'climb',
] as const;
const STEP_VIEWS: (TargetView | null)[] = [
  null, 'map', 'map', 'map', 'market', 'market', 'market', 'districts', 'pit', 'dungeons', 'rankings',
];
const STEPS: Step[] = STEP_KEYS.map((key, i) => ({
  view: STEP_VIEWS[i],
  title: t(`tutorial.steps.${key}.title`),
  body: t(`tutorial.steps.${key}.body`),
}));

const CURRENCY = {
  cash: { icon: cashIcon, get label() { return t('tutorial.currency.silver'); } },
  gold: { icon: runesIcon, get label() { return t('tutorial.currency.runes'); } },
  nerve: { icon: nerveIcon, get label() { return t('tutorial.currency.fury'); } },
} as const;
type CurrencyKey = keyof typeof CURRENCY;

/** Split a beat's body on {cash}/{gold}/{nerve} tokens so the template can
 *  drop in the real currency icon next to the word instead of bare text —
 *  QA: "no icons for each in-game currency... I don't understand it." */
function renderBody(body: string): Array<{ text: string } | { currency: CurrencyKey }> {
  const parts: Array<{ text: string } | { currency: CurrencyKey }> = [];
  const re = /\{(cash|gold|nerve)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push({ text: body.slice(last, m.index) });
    parts.push({ currency: m[1] as CurrencyKey });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

const state = ref<{ step: number; completed: boolean } | null>(null);
// `idx` is what's on screen right now (Back can move it down again); `furthest`
// is the highest beat actually walked — that's the only thing ever reported to
// the server, since progress() forbids jumping ahead but happily accepts a
// client that revisits earlier beats without re-posting them.
const idx = ref(0);
const furthest = ref(0);
const busy = ref(false);
const rewardMsg = ref('');
// True when this mount came from a manual re-open (openTutorial()), not the
// server-driven first-run flow. Replay is pure client-side navigation: it
// never calls fetchTutorial/saveTutorialStep/completeTutorial/skipTutorial,
// so a hero who already finished (and was paid) can review the walkthrough
// any time without re-touching progress or claiming a second reward.
const replay = ref(false);

const current = computed(() => STEPS[Math.min(idx.value, STEPS.length - 1)]);
const currentParts = computed(() => renderBody(current.value.body));
const isLast = computed(() => idx.value >= STEPS.length - 1);
const showMoveDemo = computed(() => idx.value === 1);

function emitNav() {
  const view = current.value.view;
  if (view) emit('navigate', view);
}

onMounted(async () => {
  if (tutorialLaunch.open) {
    // Manual re-open: always start from step 1, and never hit the server —
    // this is a review, not a fresh run.
    replay.value = true;
    idx.value = furthest.value = 0;
    state.value = { step: 0, completed: false };
    emitNav();
    return;
  }
  try {
    const s = await fetchTutorial();
    state.value = { step: s.step, completed: s.completed };
    idx.value = furthest.value = Math.min(s.step, STEPS.length - 1);
    if (s.completed) emit('done');
    else emitNav();
  } catch { emit('done'); } // non-fatal: never block the game on onboarding
});

function back() {
  if (busy.value || idx.value === 0) return;
  idx.value -= 1;
  emitNav();
}

async function next() {
  if (busy.value) return;
  if (replay.value) {
    // Client-side only: no progress save, no completion call, no reward.
    if (isLast.value) {
      closeTutorial();
      emit('navigate', 'profile');
      emit('done');
    } else {
      idx.value += 1;
      emitNav();
    }
    return;
  }
  busy.value = true;
  try {
    if (isLast.value) {
      const res = await completeTutorial();
      if (res.reward) {
        rewardMsg.value = `+${res.reward.cash} silver · +${res.reward.xp} XP`;
        emit('updated'); // the reward was credited server-side — sync the chips
      }
      emit('navigate', 'profile');
      setTimeout(() => emit('done'), res.reward ? 1400 : 0);
      if (!res.reward) emit('done');
    } else {
      idx.value += 1;
      if (idx.value > furthest.value) {
        furthest.value = idx.value;
        void saveTutorialStep(idx.value);
      }
      emitNav();
    }
  } finally { busy.value = false; }
}

async function onSkip() {
  if (busy.value) return;
  if (replay.value) {
    // Dismissible any time — no skipTutorial() call, this run was never
    // tracked server-side.
    closeTutorial();
    emit('navigate', 'profile');
    emit('done');
    return;
  }
  busy.value = true;
  try { await skipTutorial(); } catch { /* ignore */ }
  emit('navigate', 'profile');
  emit('done');
}
</script>

<template>
  <!-- Docked to the side (dominion parity) rather than a full-screen scrim —
       the whole point of step 3's per-beat navigation is that the real screen
       (map/Pit/Districts/...) stays visible and legible behind the card. -->
  <div v-if="state && !state.completed" class="fixed inset-0 z-[200] pointer-events-none">
    <div class="tut-card pointer-events-auto" style="box-shadow: var(--shadow-lift)">
      <div class="tut-strap" aria-hidden="true"></div>

      <div class="tut-body">
        <!-- carved runic tally — one glyph per beat, lit up to the current one.
             Purely decorative; the numeric "n / N" by the buttons stays the
             accessible count. -->
        <div class="tut-tally" aria-hidden="true">
          <span
            v-for="(s, i) in STEPS" :key="i"
            class="carved tut-rune"
            :class="{ 'is-lit': i <= idx }"
          >ᛁ</span>
        </div>

        <h2 class="font-heading text-[17px] gilt-text mb-2">{{ current.title }}</h2>
        <p class="font-body text-[13px] leading-relaxed mb-4" style="color: #dfe7f1">
          <template v-for="(p, i) in currentParts" :key="i"
          ><span v-if="'text' in p">{{ p.text }}</span
          ><span v-else class="curr-chip">
              <img :src="CURRENCY[p.currency].icon" :alt="CURRENCY[p.currency].label" class="curr-icon" />
              <span class="curr-label">{{ CURRENCY[p.currency].label }}</span>
            </span
          ></template>
        </p>

        <!-- Movement mini-preview — dominion parity: a small illustration beats
             prose for "click adjacent hex, pay nerve" (QA: "no preview of...
             how to move"). Purely decorative; the real map is live behind the card. -->
        <div v-if="showMoveDemo" class="move-demo" aria-hidden="true">
          <div class="move-hex move-hex--you"><span class="move-you-dot"></span></div>
          <svg class="move-arrow" viewBox="0 0 64 24" width="64" height="24">
            <path d="M2 12 H30" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-dasharray="1 5" />
            <text x="33" y="17" class="move-rune">ᛉ</text>
            <circle class="move-cursor" r="3" cy="12" />
          </svg>
          <div class="move-hex move-hex--target">
            <span class="move-cost">-1 <img :src="nerveIcon" alt="fury" /></span>
          </div>
        </div>
        <p v-if="showMoveDemo" class="move-caption">{{ t('tutorial.demoCaption') }}</p>

        <p v-if="rewardMsg" class="font-label uppercase tracking-wider text-sm text-gold-bright mb-4">
          Reward: {{ rewardMsg }}
        </p>

        <div class="flex items-center justify-between mt-2 flex-wrap gap-y-2">
          <button
            :disabled="busy"
            @click="onSkip"
            class="btn-forge btn-forge--carved btn-forge--sm shrink-0"
          >{{ t('tutorial.skip') }}</button>
          <div class="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <button
              v-if="idx > 0"
              :disabled="busy"
              @click="back"
              class="btn-forge btn-forge--carved btn-forge--sm shrink-0"
            >{{ t('tutorial.back') }}</button>
            <span class="font-label uppercase tracking-wider text-xs text-text-dim shrink-0 whitespace-nowrap">{{ idx + 1 }} / {{ STEPS.length }}</span>
            <button
              :disabled="busy"
              @click="next"
              class="btn-forge btn-forge--rune shrink-0 whitespace-nowrap"
            >{{ isLast ? t('tutorial.carveMyName') : t('tutorial.next') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Docked card: bottom sheet on narrow viewports, a right-hand rail (Burgaz's
   spot in dominion) from sm up — in both cases most of the screen behind it
   (the map, the Pit, Districts...) stays visible so the step reads as a live
   preview, not a blackout modal. */
.tut-card {
  position: absolute;
  left: 12px; right: 12px; bottom: 12px;
  max-height: min(74vh, 640px);
  overflow-y: auto;
  isolation: isolate;
}
@media (min-width: 640px) {
  .tut-card {
    left: auto; right: 20px; bottom: auto;
    top: 50%; transform: translateY(-50%);
    width: 380px; max-height: min(80vh, 600px);
  }
}

/* THE SIGNPOST FACE — a hewn plank seated behind everything at z:-1, so it
   never paints over a bare text node (the same trick as .btn-forge::before
   in app.css). `.tut-card`'s isolation:isolate is what makes -1 stay ABOVE
   the page behind the card while sitting BELOW its own content. */
.tut-card::before {
  content: '';
  position: absolute; inset: 0; z-index: -1;
  clip-path: var(--clip-hewn);
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow: var(--rim-ice-sunken), inset 0 0 0 1px rgba(201, 165, 88, 0.22);
}

/* The iron strap the signpost hangs from — sticky, so it stays welded to the
   top edge of the plank while a long beat's prose scrolls under it. */
.tut-strap {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 15px;
  background: var(--tex-forge), var(--mat-iron);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  box-shadow:
    inset 0 1px 0 rgba(226, 236, 248, 0.16),
    inset 0 -3px 6px rgba(0, 0, 0, 0.45);
}
.tut-strap::after {
  content: '';
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(circle 1.6px at 9px 50%, #9fb0c4, #414b58 55%, transparent 75%) repeat-x;
  background-size: 32px 100%;
  opacity: 0.75;
}

.tut-body { padding: 16px 20px 20px; }

/* THE TALLY — one carved rune per beat. `.carved` / `.is-lit` are the
   system-wide "cut letter, fills with aurora when active" pair (app.css);
   this just lays the row out. */
.tut-tally {
  display: flex;
  gap: 5px;
  margin-bottom: 14px;
  font-family: var(--font-heading);
  font-size: 15px;
  line-height: 1;
}
.tut-rune { opacity: 0.85; }
.tut-rune.is-lit { opacity: 1; }

/* Currency icon+name pair, inline with the beat's prose. */
.curr-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px 1px 3px; margin: 0 1px;
  border-radius: 0;
  background: rgba(200, 162, 76, 0.12);
  vertical-align: -4px;
}
.curr-icon { width: 18px; height: 18px; object-fit: contain; }
.curr-label {
  font-family: var(--font-label, monospace);
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  color: var(--color-gold-bright);
  text-transform: none;
}

/* Movement mini-demo: a "you" hex, an arrow, a target hex tagged with its
   nerve cost — a tiny animated pointer nudges along the arrow on a loop. */
.move-demo {
  display: flex; align-items: center; justify-content: center; gap: 4px;
  margin: 4px 0 8px;
}
.move-hex {
  position: relative; width: 46px; height: 40px; flex: none;
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  display: grid; place-items: center;
}
.move-hex--you {
  background: linear-gradient(180deg, rgba(200,162,76,0.35), rgba(200,162,76,0.12));
  box-shadow: inset 0 0 0 1.5px var(--color-gold);
}
.move-you-dot {
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--color-gold-bright);
  box-shadow: 0 0 10px rgba(230, 195, 110, 0.8);
}
.move-hex--target {
  background: linear-gradient(180deg, rgba(120,200,150,0.22), rgba(120,200,150,0.08));
  box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 0.7);
  animation: move-target-pulse 1.8s ease-in-out infinite;
}
.move-cost {
  display: inline-flex; align-items: center; gap: 2px;
  font-family: var(--font-label, monospace);
  font-size: 10px; font-weight: 700; color: var(--color-text);
}
.move-cost img { width: 13px; height: 13px; object-fit: contain; }
/* the cord — a dashed frost line running to the guide rune, not a plain arrow shaft */
.move-arrow { color: var(--color-ice-dim); flex: none; }
.move-rune {
  fill: var(--color-aurora);
  font-size: 15px;
  font-family: var(--font-heading);
  filter: drop-shadow(0 0 5px color-mix(in srgb, var(--color-aurora) 75%, transparent));
}
.move-cursor {
  fill: var(--color-aurora);
  animation: move-cursor-travel 1.8s ease-in-out infinite;
}
.move-caption {
  text-align: center; margin: -4px 0 14px;
  font-family: var(--font-label, monospace);
  font-size: 11px; letter-spacing: 0.06em;
  color: var(--color-text-dim);
}
@keyframes move-cursor-travel {
  0%, 15% { transform: translateX(0); opacity: 0; }
  25% { opacity: 1; }
  70% { transform: translateX(30px); opacity: 1; }
  85%, 100% { transform: translateX(30px); opacity: 0; }
}
@keyframes move-target-pulse {
  0%, 100% { box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 0.7); }
  50% { box-shadow: inset 0 0 0 1.5px rgba(140, 210, 160, 1), 0 0 14px rgba(140, 210, 160, 0.45); }
}
@media (prefers-reduced-motion: reduce) {
  .move-cursor, .move-hex--target { animation: none; }
  .move-cursor { opacity: 1; transform: translateX(17px); }
}
</style>
